// ── Game stages (plan 04) ──────────────────────────────────────

import type { PlayerTag } from './board'
import type { RaidId, RaidPlayerId } from './raid'

/**
 * Plan 04 — the kind of a raid stage. The 6 values mirror the backend
 * `raid_stage_kind` Postgres enum
 * (`apps/backend/shared/entity/src/sea_orm_active_enums.rs`). The string
 * casing matches what the planner sends over the wire (snake_case
 * identifiers); unknown / future server-side values fall back to the
 * `'custom'` literal at the type boundary so a stale client keeps rendering
 * the chip strip.
 */
export const RaidStageKind = {
  Opening: 'opening',
  Forest: 'forest',
  MidPush: 'mid_push',
  LateDefense: 'late_defense',
  Endgame: 'endgame',
  Custom: 'custom',
} as const

export type RaidStageKind = (typeof RaidStageKind)[keyof typeof RaidStageKind]

/**
 * Per-raid cap mirrors backend `invariants::MAX_STAGES_PER_RAID` (9).
 * The pre-flight checks in the store refuse `RaidStageAdd` beyond the cap
 * so the user gets a clean error before the network round-trip.
 */
export const MAX_STAGES_PER_RAID = 9

/**
 * Nominal alias for a raid-stage row id, matching the `RoomId` /
 * `RaidId` pattern. The id is the backend DB PK (`i64`) stringified for
 * the frontend convention.
 */
export type RaidStageId = string

/**
 * A single stage row in a raid's timeline (plan 04). Each raid carries
 * an ordered list of these — opening skirmish, forest, mid push, late
 * defense, end-game, plus optional custom stages for officers who want more.
 * `kind` is the canonical kind (the planner derives a default `name`
 * from the kind when officers haven't renamed the stage).
 */
export interface RaidStage {
  readonly id: RaidStageId
  readonly raidId: RaidId
  readonly kind: RaidStageKind
  readonly name: string
  readonly notes: string
  readonly orderIndex: number
}

/** A move or reorder relative to the raid's canonical ALL-stage snapshot. */
export interface RaidStagePlacementDiff {
  readonly beforeGroupId: string | null
  readonly beforeGroupName: string
  readonly beforeOrderIndex: number
  readonly afterGroupId: string
  readonly afterGroupName: string
  readonly afterOrderIndex: number
}

/** One saved-build row on either side of a stage override. */
export interface RaidStageBuildSnapshot {
  readonly orderIndex: number
  readonly weaponSlugs: ReadonlyArray<string>
}

/** The build library and active choice before and after a stage override. */
export interface RaidStageBuildDiff {
  readonly before: ReadonlyArray<RaidStageBuildSnapshot>
  readonly after: ReadonlyArray<RaidStageBuildSnapshot>
  readonly beforeActiveBuildIndex: number
  readonly afterActiveBuildIndex: number
}

/**
 * Every override for one player in one non-ALL stage. The server returns this
 * normalized, display-ready shape together with the effective raid board so
 * the client can replace the board and its audit card at one event boundary.
 */
export interface RaidStageDiff {
  readonly stageId: RaidStageId
  readonly stageName: string
  readonly playerId: RaidPlayerId
  readonly playerName: string
  readonly placement: RaidStagePlacementDiff | null
  readonly build: RaidStageBuildDiff | null
  readonly addedTags: ReadonlyArray<PlayerTag>
  readonly removedTags: ReadonlyArray<PlayerTag>
}

/**
 * Shared raid-duration bounds (plan 03). The `RaidScheduleModal` prompt
 * surfaces these in the user-facing prompt text so the officer knows the
 * legal range before typing a duration. Live here next to the other stage
 * caps because every "scheduled raid" form pairs a stage-aware template
 * with a duration clamp.
 */
export const MIN_RAID_DURATION_MINUTES = 15
export const MAX_RAID_DURATION_MINUTES = 180
export const DEFAULT_RAID_DURATION_MINUTES = 30
